import React, { createContext, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { campaignKeys } from "hooks/api/query-keys";
import { useCurrentClient } from "hooks/api/use-clients";

import { getCampaign } from "generated/api/campaign";
import type {
  CampaignDTO,
  CampaignRequest,
  CampaignStrategyPatchRequest,
  GetAllCampaignsForClientAudienceInItem,
  GetAllCampaignsForClientCampaignObjectiveInItem,
  GetAllCampaignsForClientParams,
  GetAllCampaignsForClientProgramTypeInItem,
  JsonNode,
  PagedModelCampaignSummaryDTO,
} from "generated/schemas";

import { PaginatedRequestOptions, PaginatedResponse } from "types/pagination";
import { mapPage } from "utilities/pagination";

import { FiltersToApplyOnCampaignsPage } from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.types";

const campaignApi = getCampaign();

/**
 * Plain campaign shape returned by the API.
 *
 * Extends `CampaignDTO` with `defaultThemeId` and `domainUrl`. These two fields
 * are not declared in the OpenAPI spec but the frontend currently reads/writes
 * them. Behavior is preserved during the Orval migration; the discrepancy is
 * tracked in AGENTS.md as `Needs confirmation` for the backend team.
 */
export type CampaignAttributes = CampaignDTO & {
  defaultThemeId?: string;
  domainUrl?: string;
};

/**
 * Loose write-body accepted by campaign create/replace mutations.
 *
 * Enum fields (`programType`, `campaignObjective`, etc.) are
 * typed as plain `string` because forms construct values from dictionary lookups
 * and do not narrow them to the generated string-union enums. Mutations cast
 * the body back to `CampaignRequest` before handing it to the Orval client.
 *
 * `defaultThemeId` and `domainUrl` are undocumented in the OpenAPI spec but the
 * frontend currently sends them. `lastModifiedDate` is also serialized by some
 * forms even though the backend manages it; both are preserved during the
 * Orval migration. See AGENTS.md `Needs confirmation` entry.
 */
export type CampaignWriteBody = {
  name: string;
  programType?: string;
  campaignObjective?: string;
  audiences?: string[];
  cdnSubdirectoryName?: string;
  transferStudentStatusFlag?: boolean;
  defaultThemeId?: string;
  domainUrl?: string;
  lastModifiedDate?: string;
};

/** Loose patch body for templated campaign strategy updates. */
export type CampaignPatchBody = {
  name?: string;
  strategyData?: JsonNode;
};

type CampaignScope = {
  clientId: string;
};

type CampaignDetailParams = {
  clientId?: string;
  id?: string;
};

type CampaignListParams = PaginatedRequestOptions & {
  clientId?: string;
  filters?: FiltersToApplyOnCampaignsPage;
};

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

const CurrentCampaignContext = createContext<CampaignAttributes | null>(null);

export function CurrentCampaignProvider({
  campaign,
  children,
}: React.PropsWithChildren<{ campaign: CampaignAttributes }>): React.ReactElement {
  return (
    <CurrentCampaignContext.Provider value={campaign}>{children}</CurrentCampaignContext.Provider>
  );
}

export const useCurrentCampaignId = () => {
  const contextCampaign = useContext(CurrentCampaignContext);
  const { campaignId } = useParams<{ campaignId: string }>();

  return contextCampaign?.id ?? campaignId;
};

/**
 * Map legacy `PaginatedRequestOptions` + `FiltersToApplyOnCampaignsPage` to
 * Orval's typed query params. The backend exposes typed filter operators
 * (`.in`, `.notIn`, `.specified`) only via `.in` for the multi-select fields
 * here, so single-value selections are sent as `.in` with a one-element array.
 */
function buildCampaignListParams(
  listParams: PaginatedRequestOptions,
  filters?: FiltersToApplyOnCampaignsPage,
): GetAllCampaignsForClientParams {
  const { page, size, sort, search, unpaged } = listParams;
  const params: GetAllCampaignsForClientParams = {};

  if (unpaged) {
    params.unpaged = true;
  } else {
    if (page !== undefined) params.page = page;
    if (size !== undefined) params.size = size;
  }

  if (sort) {
    params.sort = [sort];
  }

  if (search) {
    params.q = search;
  }

  if (filters) {
    if (filters.programType?.length) {
      params["programType.in"] = filters.programType as GetAllCampaignsForClientProgramTypeInItem[];
    }
    if (filters.campaignObjective?.length) {
      params["campaignObjective.in"] =
        filters.campaignObjective as GetAllCampaignsForClientCampaignObjectiveInItem[];
    }
    if (filters.audience?.length) {
      params["audience.in"] = filters.audience as GetAllCampaignsForClientAudienceInItem[];
    }
  }

  return params;
}

function normalizePageResponse(
  page: PagedModelCampaignSummaryDTO,
): PaginatedResponse<CampaignAttributes> {
  return mapPage(page, (item) => item as CampaignAttributes);
}

export const useCampaign = <TData = CampaignAttributes,>(
  params: CampaignDetailParams,
  options?: UseQueryHookOptions<CampaignAttributes, TData>,
): ReturnType<typeof useQuery<CampaignAttributes, Error, TData>> => {
  const { clientId, id } = params;

  return useQuery({
    queryKey: campaignKeys.detail(clientId ?? "", id ?? ""),
    queryFn: async () => {
      if (!clientId || !id) {
        throw new Error("Context required for useCampaign queryFn");
      }
      const dto = await campaignApi.getCampaign({ clientId, id });
      return dto as CampaignAttributes;
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(id) && (options?.enabled ?? true),
  });
};

export const useCampaigns = <TData = PaginatedResponse<CampaignAttributes>,>(
  params: CampaignListParams,
  options?: UseQueryHookOptions<PaginatedResponse<CampaignAttributes>, TData>,
): ReturnType<typeof useQuery<PaginatedResponse<CampaignAttributes>, Error, TData>> => {
  const { clientId, filters, ...listParams } = params;

  return useQuery({
    queryKey: campaignKeys.list(clientId ?? "", { ...listParams, ...(filters && { filters }) }),
    queryFn: async () => {
      if (!clientId) {
        throw new Error("Context required for useCampaigns queryFn");
      }
      const apiParams = buildCampaignListParams(listParams, filters);
      const page = await campaignApi.getAllCampaignsForClient({ clientId }, apiParams);
      return normalizePageResponse(page);
    },
    ...options,
    enabled: Boolean(clientId) && (options?.enabled ?? true),
  });
};

export function setCampaignDetailCache(
  queryClient: QueryClient,
  scope: CampaignScope,
  attributes: CampaignAttributes,
) {
  queryClient.setQueryData(campaignKeys.detail(scope.clientId, attributes.id), attributes);
  queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
}

export const useSetCampaignDetailCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (scope: CampaignScope, attributes: CampaignAttributes) => {
      setCampaignDetailCache(queryClient, scope, attributes);
    },
    [queryClient],
  );
};

export function useCurrentCampaign(): CampaignAttributes {
  const contextCampaign = useContext(CurrentCampaignContext);
  const currentClient = useCurrentClient();
  const campaignId = useCurrentCampaignId();
  const { data } = useCampaign(
    {
      clientId: currentClient.id,
      id: campaignId,
    },
    {
      enabled: !contextCampaign && !!campaignId,
    },
  );
  const attributes = contextCampaign ?? data;

  if (!attributes) {
    throw new Error("useCurrentCampaign: called before a campaign was cached");
  }

  return attributes;
}

/**
 * Mutation for creating a campaign (POST /api/clients/{clientId}/campaigns).
 * Replaces the legacy `Campaign.create` static call.
 */
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: CampaignWriteBody }) =>
      campaignApi.createCampaign(
        { clientId },
        data as unknown as CampaignRequest,
      ) as Promise<CampaignAttributes>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
    },
  });
};

/**
 * Mutation for fully replacing a campaign (PUT /api/clients/{clientId}/campaigns/{id}).
 * Replaces the legacy `Campaign.replace` static call. The PUT endpoint is named
 * `updateCampaign` in the generated Orval client.
 */
export const useReplaceCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      id,
      data,
    }: {
      clientId: string;
      id: string;
      data: CampaignWriteBody;
    }) =>
      campaignApi.updateCampaign(
        { clientId, id },
        data as unknown as CampaignRequest,
      ) as Promise<CampaignAttributes>,
    onSuccess: (data, variables) => {
      setCampaignDetailCache(queryClient, { clientId: variables.clientId }, data);
    },
  });
};

/**
 * Mutation for partially updating a templated campaign
 * (PATCH /api/clients/{clientId}/campaigns/{id}).
 */
export const usePartiallyUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      id,
      data,
    }: {
      clientId: string;
      id: string;
      data: CampaignPatchBody;
    }) =>
      campaignApi.partiallyUpdateTemplatedCampaign(
        { clientId, id },
        data as unknown as CampaignStrategyPatchRequest,
      ) as Promise<CampaignAttributes>,
    onSuccess: (data, variables) => {
      setCampaignDetailCache(queryClient, { clientId: variables.clientId }, data);
    },
  });
};
